import { API_CONFIG } from "@/config";
import { logEvent } from "@services/clientLog";
import { useAuthStore } from "@store/authStore";
import axios, {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from "axios";

// Create axios instance with centralized config
const api: AxiosInstance = axios.create({
  baseURL: API_CONFIG.baseUrl,
  timeout: API_CONFIG.timeout,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor - Add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().accessToken;

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    (config as { metadata?: { startTime: number } }).metadata = {
      startTime: Date.now(),
    };

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor - Handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;

    const startTime = (
      error.config as { metadata?: { startTime: number } } | undefined
    )?.metadata?.startTime;
    logEvent({
      kind: "network",
      msg: `${error.config?.method?.toUpperCase() ?? "REQ"} ${
        error.config?.url ?? ""
      } failed`,
      url: `${error.config?.baseURL ?? ""}${error.config?.url ?? ""}`,
      status: error.response?.status,
      code: error.code,
      ms: startTime ? Date.now() - startTime : undefined,
      detail: error.message,
    });

    // Network error or timeout (e.g. Render cold start exceeding axios timeout):
    // do NOT logout — propagate so callers can show retry/loading states.
    // Treating these as auth failures caused redirect loops on slow networks.
    const isNetworkOrTimeout =
      !error.response ||
      error.code === "ECONNABORTED" ||
      error.code === "ETIMEDOUT" ||
      (typeof error.message === "string" &&
        error.message.toLowerCase().includes("network"));

    if (isNetworkOrTimeout) {
      return Promise.reject(error);
    }

    // If error is 401 and we haven't tried refreshing yet
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !(originalRequest as unknown as { _retry: boolean })._retry
    ) {
      (originalRequest as unknown as { _retry: boolean })._retry = true;

      const refreshToken = useAuthStore.getState().refreshToken;

      if (refreshToken) {
        try {
          const response = await axios.post(
            `${API_CONFIG.baseUrl}/auth/token/refresh/`,
            {
              refresh: refreshToken,
            },
          );

          const { access } = response.data;
          useAuthStore.getState().setTokens(access, refreshToken);

          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${access}`;
          }

          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed: clear session. ProtectedRoute observes
          // isAuthenticated and navigates via React Router — no hard reload.
          console.log("[API] Token refresh failed - clearing session");
          useAuthStore.getState().logout();
          return Promise.reject(refreshError);
        }
      }
    }

    return Promise.reject(error);
  },
);

export default api;

// Friendly, non-technical message for an HTTP status the backend didn't
// explain itself. Never exposes raw codes like "405" to the user.
function messageForStatus(status: number): string {
  switch (status) {
    case 400:
      return "Something went wrong. Please check your details and try again.";
    case 401:
      return "Incorrect email or password.";
    case 403:
      return "You don't have permission to access this.";
    case 404:
      return "We couldn't find what you were looking for.";
    case 429:
      return "Too many attempts. Please wait a moment and try again.";
    default:
      if (status >= 500) {
        return "The server is unavailable right now. Please try again shortly.";
      }
      return "Temporary connection problem with the server. Please try again shortly.";
  }
}

// Turns any thrown error into a clear, human-readable message.
export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{
      detail?: string;
      message?: string;
    }>;

    // A message the backend explicitly sent is already meaningful — use it.
    const backendMessage =
      axiosError.response?.data?.detail || axiosError.response?.data?.message;
    if (backendMessage) {
      return backendMessage;
    }

    // No response, timeout or network drop → a connection problem, not the app.
    const isNetworkOrTimeout =
      !axiosError.response ||
      axiosError.code === "ECONNABORTED" ||
      axiosError.code === "ETIMEDOUT" ||
      (typeof axiosError.message === "string" &&
        axiosError.message.toLowerCase().includes("network"));
    const status = axiosError.response?.status;
    if (isNetworkOrTimeout || !status) {
      return "Couldn't connect to the server. Check your internet and try again.";
    }

    return messageForStatus(status);
  }

  return "Something unexpected happened. Please try again.";
}
